import React, { useState } from "react";
import { useGeofence } from "@/contexts/GeofenceContext";
import { useAuth } from "@/contexts/AuthContext";
import { MapPin, X, Navigation, CheckCircle2, Crosshair, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

export default function ResidentAwayBanner() {
    const { status, distanceMeters, societyConfig, residentWarningDismissed, dismissResidentWarning, coords, refreshConfig, checkLocation } = useGeofence();
    const { user } = useAuth();
    const [calibrating, setCalibrating] = useState(false);

    const canCalibrate = Boolean(user?.role === "admin" || user?.email === "neighbornest.in@gmail.com");

    if (!societyConfig.enabled || !societyConfig.resident_warning) return null;
    if (residentWarningDismissed) return null;
    if (status !== "outside") return null;

    const formatDistance = (meters) => {
        if (meters == null) return null;
        if (meters < 1000) return `${meters}m`;
        return `${(meters / 1000).toFixed(1)} km`;
    };

    const distStr = formatDistance(distanceMeters);

    const handleCalibrateHere = async () => {
        if (!coords?.latitude || !coords?.longitude) {
            toast.error("GPS position not acquired. Please wait a moment.");
            return;
        }
        setCalibrating(true);
        try {
            await api.post("/society/geofence", {
                society_name: societyConfig.society_name,
                full_name: societyConfig.full_name,
                address: societyConfig.address,
                latitude: coords.latitude,
                longitude: coords.longitude,
                radius_meters: Math.max(Number(societyConfig.radius_meters) || 1500, 1500),
                guard_strict: true,
                resident_warning: true,
                enabled: true
            });
            toast.success("Success! Pyramid City 3 calibrated to your current GPS position.");
            await refreshConfig();
            checkLocation();
        } catch (err) {
            toast.error("Could not save calibration coordinates.");
        } finally {
            setCalibrating(false);
        }
    };

    return (
        <div className="max-w-7xl mx-auto w-full px-3.5 sm:px-6 lg:px-8 mb-3" data-testid="resident-away-banner">
            <div className="rounded-2xl border border-amber-300/80 bg-gradient-to-r from-amber-50 via-amber-50/80 to-orange-50/60 p-3 sm:p-3.5 shadow-xs flex items-center justify-between gap-3 text-nest-ink">
                <div className="flex items-center gap-3 min-w-0">
                    <div className="h-9 w-9 rounded-xl bg-amber-500/15 border border-amber-400/40 flex items-center justify-center shrink-0 text-amber-800">
                        <MapPin size={18} />
                    </div>
                    <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-xs sm:text-sm font-bold text-amber-950">
                                Away from {societyConfig.society_name}
                            </span>
                            {distStr && (
                                <span className="text-[10px] font-mono font-bold px-2 py-0.5 rounded-full bg-amber-200/80 text-amber-900 border border-amber-300">
                                    ~{distStr} away
                                </span>
                            )}
                            <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 border border-emerald-300 flex items-center gap-1">
                                <CheckCircle2 size={10} />
                                Remote Access Active
                            </span>
                            {canCalibrate && coords && (
                                <button
                                    type="button"
                                    onClick={handleCalibrateHere}
                                    disabled={calibrating}
                                    className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white flex items-center gap-1 cursor-pointer transition shadow-2xs"
                                    title="Click if you are currently at Pyramid City 3 to set this spot as society center"
                                >
                                    {calibrating ? <Loader2 size={10} className="animate-spin" /> : <Crosshair size={10} />}
                                    <span>I am at Pyramid City 3 (Calibrate GPS)</span>
                                </button>
                            )}
                        </div>
                        <p className="text-[11px] sm:text-xs text-amber-900/80 truncate mt-0.5">
                            You are away from Besa-Pipla Road, Nagpur. You can still approve gate entries, pay maintenance, and view notices.
                        </p>
                    </div>
                </div>

                <button
                    type="button"
                    onClick={dismissResidentWarning}
                    className="p-1.5 rounded-xl hover:bg-amber-200/60 text-amber-800 transition cursor-pointer shrink-0"
                    title="Dismiss notice"
                    aria-label="Dismiss notice"
                >
                    <X size={15} />
                </button>
            </div>
        </div>
    );
}

